import { Sparkle, ShieldCheck, DeviceTablet, type Icon as PhosphorIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { OnboardingLabels } from "./types";

export interface OnboardingLayoutProps {
  labels: OnboardingLabels;
  children: React.ReactNode;
  resumeBanner?: React.ReactNode;
  loginHref?: string;
  onLoginClick?: () => void;
  className?: string;
}

const HERO_ICONS: PhosphorIcon[] = [Sparkle, ShieldCheck, DeviceTablet];

export function OnboardingLayout({
  labels,
  children,
  resumeBanner,
  loginHref,
  onLoginClick,
  className,
}: OnboardingLayoutProps) {
  return (
    <div className={cn("min-h-screen bg-gradient-to-br from-biz-surface via-biz-background to-biz-surface dark:from-biz-background dark:via-biz-background dark:to-biz-surface", className)}>
      <div className="mx-auto flex min-h-screen w-full max-w-[1400px] flex-col px-5 py-6 sm:px-8 sm:py-10 lg:px-12">
        <header className="flex items-center justify-between gap-4">
          <a
            href="/"
            className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-biz-primary text-biz-primary-foreground shadow-biz-md">
              <span className="font-display text-base font-bold leading-none">P</span>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-xs font-medium uppercase tracking-wider text-biz-foreground-muted">{labels.hero.eyebrow}</span>
            </span>
          </a>
          <a
            href={loginHref || "#"}
            onClick={(e) => {
              if (onLoginClick) {
                e.preventDefault();
                onLoginClick();
              }
            }}
            className="rounded-md px-3 py-2 text-sm font-medium text-biz-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2"
          >
            {labels.loginLink}
          </a>
        </header>

        {resumeBanner && <div className="mt-6">{resumeBanner}</div>}

        <main className="mt-8 grid flex-1 gap-10 lg:mt-12 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:gap-16">
          {/* Hero — hidden on mobile to save real estate */}
          <aside className="hidden flex-col justify-between lg:flex">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-biz-primary">
                {labels.hero.eyebrow}
              </p>
              <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-biz-foreground xl:text-[2.75rem]">
                {labels.hero.headline}
              </h1>
              <p className="mt-3 text-base text-biz-foreground-muted">{labels.meta.pageSubtitle}</p>

              <ul className="mt-10 space-y-4">
                {labels.hero.bullets.map((bullet, i) => {
                  const Icon: PhosphorIcon = HERO_ICONS[i] ?? Sparkle;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-biz-primary/10 text-biz-primary">
                        <Icon size={18} aria-hidden />
                      </span>
                      <span className="text-sm leading-relaxed text-biz-foreground">{bullet}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-10 rounded-biz-card border border-biz-border/60 bg-biz-surface/60 p-4 backdrop-blur">
              <p className="text-sm text-biz-foreground-muted">{labels.hero.socialProof}</p>
            </div>
          </aside>

          {/* Form column */}
          <section className="flex flex-col">
            <div className="mb-6 lg:hidden">
              <h1 className="font-display text-2xl font-bold tracking-tight text-biz-foreground">{labels.meta.pageTitle}</h1>
              <p className="mt-1 text-sm text-biz-foreground-muted">{labels.meta.pageSubtitle}</p>
            </div>
            {children}
          </section>
        </main>
      </div>
    </div>
  );
}
